import { useState, useEffect, useCallback, useRef, useMemo, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { isWerkgroepIntakePlaceholderRecipientEmail, WERKGROEP_INTAKE_LINK_VALIDITY_DAYS } from "@domaddress/shared";
import { api } from "../lib/api";
import HelpHint from "../components/HelpHint";
import { MDR_UNREAD_REFRESH } from "../lib/memberDetailRequestsEvents";

type RequestKind = "contact_detail" | "member_add";

type RowIn = {
  id: number;
  created_at: string;
  group_id: number;
  group_name: string;
  requester_name: string;
  requester_email: string;
  target_label: string;
  message: string | null;
  read_by_chair_at: string | null;
  resolution: string | null;
  role: "inbox";
  request_kind: RequestKind;
};

type RowOut = {
  id: number;
  created_at: string;
  group_id: number;
  group_name: string;
  target_label: string;
  message: string | null;
  read_by_chair_at: string | null;
  read_by_requester_at: string | null;
  response_unread?: boolean;
  resolution: string | null;
  role: "outbox";
  request_kind: RequestKind;
};

type MergedRow = RowIn | RowOut;

// ── Werkgroep-intake types ────────────────────────────────────────────────────

type IntakeStatus = "issued" | "submitted" | "approved" | "rejected";

type IntakeRow = {
  id: number;
  created_at: string;
  expires_at: string;
  group_id: number;
  group_name: string;
  recipient_email: string;
  status: IntakeStatus;
  chair_first_name: string | null;
  chair_tussenvoegsel: string | null;
  chair_last_name: string | null;
  leden_count: number | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  resolution: string | null;
  sender_name: string | null;
};

type LidCandidate = {
  id: number;
  display_name: string;
  email_hint: string | null;
  match_reason: string;
  score: number;
};

type IntakeLid = {
  id: number;
  sort_order: number;
  first_name: string;
  tussenvoegsel: string | null;
  last_name: string;
  email: string | null;
  phone: string | null;
  matched_contact_id: number | null;
  created_contact_id: number | null;
  force_new_contact?: boolean;
  /** Secretaris: bij goedkeuren alleen koppeling vastleggen, geen nieuwe/gewijzigde logica op dit lid. */
  skip_on_approve?: boolean;
  candidates?: LidCandidate[];
  /** Server: matched contact of een kandidaat staat al in contact_groups voor deze werkgroep. */
  already_in_group?: boolean;
};

type IntakeDetail = IntakeRow & {
  chair_last_name: string | null;
  chair_email: string | null;
  chair_phone: string | null;
  email_body_snapshot: string | null;
  reviewer_user_id: number | null;
  review_note: string | null;
  revision_feedback?: string | null;
  chair_matched_contact_id?: number | null;
  chair_force_new_contact?: boolean;
  chair_skip_on_approve?: boolean;
  chair_candidates?: LidCandidate[];
  chair_already_in_group?: boolean;
  /** Server: alle regels groen — intake kan zonder adresboekmutaties worden afgerond. */
  can_mark_no_op?: boolean;
  leden: IntakeLid[];
};

type GroupListItem = { id: number; name: string };

type GeneratedIntakeLink = {
  url: string;
  intake_id: number;
  recipient_email: string;
};

function readApiError(err: unknown): string {
  if (isAxiosError(err)) {
    const msg = (err.response?.data as { error?: string } | undefined)?.error;
    if (msg) return msg;
  }
  return "Er ging iets mis. Probeer het opnieuw.";
}

// ── helpers ───────────────────────────────────────────────────────────────────

function resolutionLabel(r: string | null): string {
  if (!r) return "";
  if (r === "approved") return "Goedgekeurd";
  if (r === "approved_no_changes") return "Afgehandeld (geen adresboekwijzigingen)";
  if (r === "rejected") return "Afgewezen";
  if (r === "acknowledged") return "Afgehandeld (geen goed- of afkeur)";
  return r;
}

function kindLabel(k: RequestKind): string {
  if (k === "member_add") return "Groepslid";
  return "Contactgegevens";
}

function intakeStatusLabel(s: IntakeStatus): string {
  if (s === "issued") return "Verstuurd";
  if (s === "submitted") return "Ingediend";
  if (s === "approved") return "Goedgekeurd";
  if (s === "rejected") return "Afgewezen";
  return s;
}

/** Querystring voor GET /api/werkgroep-intakes (meerdere status=). */
function werkgroepIntakesApiQuery(statuses: IntakeStatus[]): string {
  const params = new URLSearchParams();
  for (const s of statuses) params.append("status", s);
  const q = params.toString();
  return q ? `?${q}` : "";
}

const tabBadgeStyle: CSSProperties = {
  marginLeft: 8,
  fontSize: "0.7rem",
  background: "var(--accent, #ee2361)",
  color: "#fff",
  borderRadius: 999,
  padding: "0.05rem 0.45rem",
  minWidth: "1.1rem",
  textAlign: "center",
  lineHeight: 1.35,
};

function rowAttentionStyle(active: boolean): CSSProperties | undefined {
  if (!active) return undefined;
  return {
    borderLeft: "4px solid var(--accent, #ee2361)",
    background: "color-mix(in srgb, var(--accent, #ee2361) 8%, transparent)",
  };
}

/** Oppervlak per ingediend lid / voorzitter: groen als al groepslid, anders licht rood (aandacht). */
function intakeLidCardSurface(opts: {
  status: IntakeStatus;
  forceNewContact?: boolean;
  alreadyInGroup?: boolean;
}): CSSProperties {
  const base: CSSProperties = {
    borderRadius: 10,
    padding: "0.65rem 0.75rem",
  };
  if (opts.status !== "submitted" || opts.forceNewContact) {
    return {
      ...base,
      border: "1px solid var(--surface-300, #e4dfe8)",
      background: "var(--surface-100, #faf8fc)",
    };
  }
  if (opts.alreadyInGroup) {
    return {
      ...base,
      border: "1px solid #6ee7b7",
      background: "#ecfdf5",
    };
  }
  return {
    ...base,
    border: "1px solid #fca5a5",
    background: "#fef2f2",
  };
}

// ── Werkgroep intake detail panel ─────────────────────────────────────────────

function IntakeDetailPanel({
  intakeId,
  intake,
  onApprove,
  onReject,
  onMarkNoOp,
  onRefreshDetail,
  onRevisionSent,
  busy,
}: {
  intakeId: number;
  intake: IntakeDetail;
  onApprove: (id: number, note: string) => void;
  onReject: (id: number, note: string) => void;
  onMarkNoOp: (id: number) => Promise<void>;
  onRefreshDetail: () => Promise<void>;
  onRevisionSent: () => Promise<void>;
  busy: boolean;
}) {
  const [note, setNote] = useState("");
  const [revisionMsg, setRevisionMsg] = useState("");
  const [patchBusy, setPatchBusy] = useState<number | "revision" | "chair" | null>(null);

  async function patchLidMatch(
    lidId: number,
    body: {
      matched_contact_id?: number | null;
      force_new_contact?: boolean;
      skip_on_approve?: boolean;
    }
  ) {
    setPatchBusy(lidId);
    try {
      await api.patch(`/api/werkgroep-intakes/${intakeId}/leden/${lidId}/match`, body);
      await onRefreshDetail();
    } catch {
      /* 403 */
    } finally {
      setPatchBusy(null);
    }
  }

  async function patchChairMatch(body: {
    matched_contact_id?: number | null;
    force_new_contact?: boolean;
    skip_on_approve?: boolean;
  }) {
    setPatchBusy("chair");
    try {
      await api.patch(`/api/werkgroep-intakes/${intakeId}/chair-match`, body);
      await onRefreshDetail();
    } catch {
      /* 403 */
    } finally {
      setPatchBusy(null);
    }
  }

  async function sendRevisionRequest() {
    const msg = revisionMsg.trim();
    if (!msg) return;
    setPatchBusy("revision");
    try {
      await api.post(`/api/werkgroep-intakes/${intakeId}/request-revision`, { message: msg });
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(MDR_UNREAD_REFRESH));
      }
      setRevisionMsg("");
      await onRevisionSent();
    } catch {
      /* error */
    } finally {
      setPatchBusy(null);
    }
  }

  const matchReasonNl = (r: string) => (r === "email" ? "zelfde e-mail" : "vergelijkbare naam");

  return (
    <div style={{ marginTop: "0.75rem" }}>
      {intake.revision_feedback?.trim() && (
        <div
          style={{
            marginBottom: "0.75rem",
            padding: "0.65rem 0.85rem",
            background: "#fffbeb",
            border: "1px solid #fcd34d",
            borderRadius: 8,
            fontSize: "0.85rem",
          }}
        >
          <strong>Laatste terugkoppeling aan voorzitter:</strong>
          <p style={{ margin: "0.35rem 0 0", whiteSpace: "pre-wrap" }}>{intake.revision_feedback}</p>
        </div>
      )}

      {intake.chair_first_name && intake.chair_last_name && (
        <div
          style={{
            marginBottom: "0.85rem",
            ...intakeLidCardSurface({
              status: intake.status,
              forceNewContact: intake.chair_force_new_contact,
              alreadyInGroup: intake.chair_already_in_group,
            }),
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
            <strong style={{ fontSize: "0.88rem" }}>Voorzitter (ook groepslid na goedkeuring)</strong>
            <span style={{ fontSize: "0.78rem", color: "#5c526d" }}>
              {intake.chair_force_new_contact && (
                <span className="group-badge" style={{ marginRight: 6, background: "#7c3aed", color: "#fff", fontSize: "0.65rem" }}>
                  Altijd nieuw contact
                </span>
              )}
              {intake.status === "submitted" && !intake.chair_force_new_contact && intake.chair_already_in_group && (
                <span className="group-badge" style={{ marginRight: 6, background: "#047857", color: "#fff", fontSize: "0.65rem" }}>
                  Al lid van groep
                </span>
              )}
              {intake.chair_skip_on_approve && (
                <span className="group-badge" style={{ marginRight: 6, background: "#0369a1", color: "#fff", fontSize: "0.65rem" }}>
                  Overslaan bij goedkeuren
                </span>
              )}
              {!intake.chair_force_new_contact && intake.chair_matched_contact_id != null && (
                <span className="group-badge" style={{ marginRight: 6, background: "#059669", color: "#fff", fontSize: "0.65rem" }}>
                  Koppeling #{intake.chair_matched_contact_id}
                </span>
              )}
            </span>
          </div>
          <div style={{ fontSize: "0.82rem", marginBottom: 8 }}>
            {[intake.chair_first_name, intake.chair_tussenvoegsel, intake.chair_last_name].filter(Boolean).join(" ")}
            {intake.chair_email && (
              <>
                {" "}
                —{" "}
                <a href={`mailto:${intake.chair_email}`} className="text-link">
                  {intake.chair_email}
                </a>
              </>
            )}
            {intake.chair_phone && <span style={{ opacity: 0.75 }}> · {intake.chair_phone}</span>}
          </div>

          {intake.status === "submitted" && (
            <div style={{ marginTop: 6 }}>
              {(intake.chair_candidates?.length ?? 0) > 0 && (
                <p style={{ fontSize: "0.78rem", color: "#453a58", marginBottom: 6 }}>
                  Mogelijke matches in het adresboek ({matchReasonNl(intake.chair_candidates![0].match_reason)}):
                </p>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginBottom: 6 }}>
                {(intake.chair_candidates ?? []).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={busy || patchBusy === "chair"}
                    title={c.email_hint ?? ""}
                    onClick={() => {
                      void patchChairMatch({ matched_contact_id: c.id, force_new_contact: false });
                    }}
                  >
                    Koppel #{c.id} · {c.display_name}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={busy || patchBusy === "chair"}
                  onClick={() => {
                    void patchChairMatch({ force_new_contact: true });
                  }}
                >
                  Nieuw contact (negeer match)
                </button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={busy || patchBusy === "chair"}
                  onClick={() => {
                    void patchChairMatch({ matched_contact_id: null, force_new_contact: false });
                  }}
                >
                  Automatisch bij goedkeuring
                </button>
                {intake.chair_already_in_group && !intake.chair_force_new_contact && (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={busy || patchBusy === "chair"}
                    onClick={() => {
                      void patchChairMatch(
                        intake.chair_skip_on_approve
                          ? { skip_on_approve: false }
                          : { skip_on_approve: true }
                      );
                    }}
                  >
                    {intake.chair_skip_on_approve
                      ? "Annuleer overslaan (voorzitter)"
                      : "Overslaan bij goedkeuren (voorzitter)"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {intake.leden.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem", marginBottom: "0.85rem" }}>
          {intake.leden.map((lid) => (
            <div
              key={lid.id}
              style={intakeLidCardSurface({
                status: intake.status,
                forceNewContact: lid.force_new_contact,
                alreadyInGroup: lid.already_in_group,
              })}
            >
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                <strong style={{ fontSize: "0.88rem" }}>
                  #{lid.sort_order}{" "}
                  {[lid.first_name, lid.tussenvoegsel, lid.last_name].filter(Boolean).join(" ")}
                </strong>
                <span style={{ fontSize: "0.78rem", color: "#5c526d" }}>
                  {lid.force_new_contact && (
                    <span className="group-badge" style={{ marginRight: 6, background: "#7c3aed", color: "#fff", fontSize: "0.65rem" }}>
                      Altijd nieuw contact
                    </span>
                  )}
                  {intake.status === "submitted" && !lid.force_new_contact && lid.already_in_group && (
                    <span className="group-badge" style={{ marginRight: 6, background: "#047857", color: "#fff", fontSize: "0.65rem" }}>
                      Al lid van groep
                    </span>
                  )}
                  {lid.skip_on_approve && (
                    <span className="group-badge" style={{ marginRight: 6, background: "#0369a1", color: "#fff", fontSize: "0.65rem" }}>
                      Overslaan bij goedkeuren
                    </span>
                  )}
                  {!lid.force_new_contact && lid.matched_contact_id && (
                    <span className="group-badge" style={{ marginRight: 6, background: "#059669", color: "#fff", fontSize: "0.65rem" }}>
                      Koppeling #{lid.matched_contact_id}
                    </span>
                  )}
                </span>
              </div>
              <div style={{ fontSize: "0.82rem", color: "#5c526d", marginBottom: 8 }}>
                {lid.email ? (
                  <a href={`mailto:${lid.email}`} className="text-link">
                    {lid.email}
                  </a>
                ) : (
                  "—"
                )}
                {lid.phone && <span> · {lid.phone}</span>}
              </div>

              {intake.status === "submitted" && (
                <div style={{ marginTop: 6 }}>
                  {(lid.candidates?.length ?? 0) > 0 && (
                    <p style={{ fontSize: "0.78rem", color: "#453a58", marginBottom: 6 }}>
                      Mogelijke matches in het adresboek ({matchReasonNl(lid.candidates![0].match_reason)}):
                    </p>
                  )}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginBottom: 6 }}>
                    {(lid.candidates ?? []).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={busy || patchBusy === lid.id}
                        title={c.email_hint ?? ""}
                        onClick={() => {
                          void patchLidMatch(lid.id, { matched_contact_id: c.id, force_new_contact: false });
                        }}
                      >
                        Koppel #{c.id} · {c.display_name}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={busy || patchBusy === lid.id}
                      onClick={() => {
                        void patchLidMatch(lid.id, { force_new_contact: true });
                      }}
                    >
                      Nieuw contact (negeer match)
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={busy || patchBusy === lid.id}
                      onClick={() => {
                        void patchLidMatch(lid.id, { matched_contact_id: null, force_new_contact: false });
                      }}
                    >
                      Automatisch bij goedkeuring
                    </button>
                    {lid.already_in_group && !lid.force_new_contact && (
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={busy || patchBusy === lid.id}
                        onClick={() => {
                          void patchLidMatch(
                            lid.id,
                            lid.skip_on_approve ? { skip_on_approve: false } : { skip_on_approve: true }
                          );
                        }}
                      >
                        {lid.skip_on_approve ? "Annuleer overslaan" : "Overslaan bij goedkeuren"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {intake.status === "submitted" && intake.can_mark_no_op && (
        <div
          style={{
            marginTop: "0.75rem",
            padding: "0.65rem 0.75rem",
            borderRadius: 10,
            border: "1px solid #6ee7b7",
            background: "#ecfdf5",
            fontSize: "0.82rem",
          }}
        >
          <strong>Alles al op orde in het adresboek</strong>
          <p style={{ margin: "0.35rem 0 0.5rem", color: "#374151" }}>
            Alle personen op deze lijst (inclusief voorzitter) staan al als groepslid in de groep. U kunt de intake
            afsluiten zonder wijzigingen aan contactkaarten.
          </p>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy || patchBusy != null}
            onClick={() => {
              void onMarkNoOp(intake.id);
            }}
          >
            Markeer als afgehandeld (geen adresboekwijzigingen)
          </button>
        </div>
      )}

      {intake.status === "submitted" && (
        <>
          <div
            style={{
              marginTop: "0.85rem",
              padding: "0.75rem",
              border: "1px dashed var(--surface-400, #c9c2d4)",
              borderRadius: 10,
              background: "#fff",
            }}
          >
            <strong style={{ fontSize: "0.85rem" }}>Terug naar voorzitter</strong>
            <p style={{ fontSize: "0.78rem", color: "#5c526d", margin: "0.35rem 0 0.5rem" }}>
              Stuur het formulier opnieuw met een vraag of correctie. De voorzitter krijgt een nieuwe link per e-mail
              (de oude link werkt dan niet meer).
            </p>
            <textarea
              className="form-input"
              value={revisionMsg}
              onChange={(e) => setRevisionMsg(e.target.value)}
              rows={3}
              maxLength={4000}
              style={{ width: "100%", boxSizing: "border-box", fontSize: "0.85rem", marginBottom: 8 }}
              placeholder="Bijvoorbeeld: graag telefoonnummer toevoegen voor …"
            />
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={busy || patchBusy === "revision" || !revisionMsg.trim()}
              onClick={() => {
                void sendRevisionRequest();
              }}
            >
              {patchBusy === "revision" ? "Versturen…" : "Vraag correctie (nieuwe link)"}
            </button>
          </div>

          <div style={{ marginTop: "0.75rem" }}>
            <label style={{ fontSize: "0.82rem", color: "#5c526d" }}>
              Toelichting bij goedkeuren/afwijzen (optioneel):
            </label>
            <textarea
              className="form-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={1000}
              style={{ marginTop: 4, resize: "vertical", fontSize: "0.85rem", width: "100%", boxSizing: "border-box" }}
              placeholder="Optioneel bericht bij afhandeling…"
            />
          </div>
          <p style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.6rem" }}>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy || patchBusy != null}
              onClick={() => onApprove(intake.id, note)}
            >
              ✓ Goedkeuren &amp; verwerken
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={busy || patchBusy != null}
              onClick={() => onReject(intake.id, note)}
            >
              ✕ Afwijzen
            </button>
          </p>
        </>
      )}

      {(intake.status === "approved" || intake.status === "rejected") && (
        <p className="page-subtitle" style={{ margin: "0.5rem 0" }}>
          <strong>Status:</strong> {resolutionLabel(intake.resolution)} —{" "}
          {intake.reviewed_at ? new Date(intake.reviewed_at).toLocaleString("nl-NL") : ""}
          {intake.review_note && <span> · {intake.review_note}</span>}
        </p>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AanvragenPage() {
  const [searchParams] = useSearchParams();
  const intakeIdFromUrl = useMemo(() => {
    const raw = searchParams.get("intake");
    const n = raw ? Number.parseInt(raw, 10) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [searchParams]);

  const initialTabFromUrl = useRef(false);
  const initialTabAuto = useRef(false);
  const deeplinkIntakeAppliedFor = useRef<number | null>(null);

  const [scope, setScope] = useState<"inbox" | "outbox" | "intakes" | "ledenlinks">("inbox");
  const [rows, setRows] = useState<MergedRow[]>([]);
  const [intakes, setIntakes] = useState<IntakeRow[]>([]);
  /** Standaard: ingediend + goedgekeurd; dubbele “verstuurd”-regels worden op de server verborgen (per groep de nieuwste). */
  const [intakeStatusFilters, setIntakeStatusFilters] = useState<IntakeStatus[]>(["submitted", "approved"]);
  const [groups, setGroups] = useState<GroupListItem[]>([]);
  const [linkByGroupId, setLinkByGroupId] = useState<Record<number, GeneratedIntakeLink>>({});
  const [linkGenBusy, setLinkGenBusy] = useState<number | "bulk" | null>(null);
  const [bulkErrors, setBulkErrors] = useState<{ group_id: number; group_name: string; error: string }[]>([]);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [expandedIntakeId, setExpandedIntakeId] = useState<number | null>(null);
  const [intakeDetail, setIntakeDetail] = useState<IntakeDetail | null>(null);
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unreadSplit, setUnreadSplit] = useState<{
    inbox: number;
    outbox: number;
    intakes_pending: number;
  } | null>(null);

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const r = await api.get<{
        data: { count?: number; inbox?: number; outbox?: number; intakes_pending?: number };
      }>("/api/member-detail-requests/unread-count");
      const d = r.data.data;
      setUnreadSplit({
        inbox: d?.inbox ?? 0,
        outbox: d?.outbox ?? 0,
        intakes_pending: d?.intakes_pending ?? 0,
      });
    } catch {
      setUnreadSplit({ inbox: 0, outbox: 0, intakes_pending: 0 });
    }
  }, []);

  useEffect(() => {
    void fetchUnreadCounts();
    const onRefresh = () => { void fetchUnreadCounts(); };
    window.addEventListener(MDR_UNREAD_REFRESH, onRefresh);
    return () => window.removeEventListener(MDR_UNREAD_REFRESH, onRefresh);
  }, [fetchUnreadCounts]);

  useEffect(() => {
    if (initialTabFromUrl.current) return;
    const t = searchParams.get("tab");
    if (t === "outbox" || t === "inbox" || t === "intakes" || t === "ledenlinks") {
      setScope(t);
      initialTabFromUrl.current = true;
      initialTabAuto.current = true;
    }
  }, [searchParams]);

  /** `?intake=` opent de werkgroep-intakes-tab en klapt die intake open (o.a. vanuit activiteitenlog). */
  useEffect(() => {
    if (intakeIdFromUrl == null) {
      deeplinkIntakeAppliedFor.current = null;
      return;
    }
    setScope("intakes");
    // Voorkom dat de “automatisch naar outbox”-logica deze tab overschrijft bij alleen ?intake= zonder ?tab=.
    initialTabAuto.current = true;
  }, [intakeIdFromUrl]);

  useEffect(() => {
    if (intakeIdFromUrl == null) return;
    if (scope !== "intakes" || loading) return;
    if (deeplinkIntakeAppliedFor.current === intakeIdFromUrl) return;
    deeplinkIntakeAppliedFor.current = intakeIdFromUrl;
    setExpandedIntakeId(intakeIdFromUrl);
    setIntakeDetail(null);
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<{ data: IntakeDetail }>(`/api/werkgroep-intakes/${intakeIdFromUrl}`);
        if (!cancelled) setIntakeDetail(res.data.data);
      } catch {
        if (!cancelled) setIntakeDetail(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [intakeIdFromUrl, scope, loading]);

  useEffect(() => {
    if (initialTabFromUrl.current || initialTabAuto.current) return;
    if (unreadSplit === null) return;
    if (unreadSplit.inbox === 0 && unreadSplit.outbox > 0) {
      setScope("outbox");
    }
    initialTabAuto.current = true;
  }, [unreadSplit]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (scope === "intakes") {
        const q = werkgroepIntakesApiQuery(intakeStatusFilters);
        const res = await api.get<{ data: IntakeRow[] }>(`/api/werkgroep-intakes${q}`);
        setIntakes(res.data.data ?? []);
        void fetchUnreadCounts();
      } else if (scope === "ledenlinks") {
        setBulkErrors([]);
        const res = await api.get<{ data: GroupListItem[] }>("/api/groups");
        const list = (res.data.data ?? []).map((g) => ({ id: g.id, name: g.name }));
        list.sort((a, b) => a.name.localeCompare(b.name, "nl"));
        setGroups(list);
        void fetchUnreadCounts();
      } else {
        const [mdr, gma] = await Promise.all([
          api.get(`/api/member-detail-requests?scope=${scope}`),
          api.get(`/api/group-member-add-requests?scope=${scope}`),
        ]);
        const a: MergedRow[] = (mdr.data.data ?? []).map((r: RowIn | RowOut) => ({
          ...r,
          request_kind: (r as { request_kind?: RequestKind }).request_kind ?? ("contact_detail" as RequestKind),
        }));
        const b: MergedRow[] = (gma.data.data ?? []).map((r: RowIn | RowOut) => ({
          ...r,
          request_kind: (r as { request_kind?: RequestKind }).request_kind ?? ("member_add" as RequestKind),
        }));
        const merged = [...a, ...b].sort(
          (x, y) => new Date(y.created_at).getTime() - new Date(x.created_at).getTime()
        );
        setRows(merged);
        if (scope === "outbox") {
          try {
            await api.post("/api/member-detail-requests/mark-my-responses-seen");
            setRows((prev) =>
              prev.map((r) => {
                if (r.role === "outbox" && r.response_unread) {
                  return { ...r, response_unread: false, read_by_requester_at: new Date().toISOString() };
                }
                return r;
              })
            );
          } catch { /* badge blijft */ }
          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event(MDR_UNREAD_REFRESH));
          }
        }
        void fetchUnreadCounts();
      }
    } finally {
      setLoading(false);
    }
  }, [scope, intakeStatusFilters, fetchUnreadCounts]);

  useEffect(() => { void load(); }, [load]);

  async function resolveRow(row: MergedRow, action: "approve" | "reject" | "acknowledge") {
    const path =
      row.request_kind === "member_add"
        ? `/api/group-member-add-requests/${row.id}/resolve`
        : `/api/member-detail-requests/${row.id}/resolve`;
    try { await api.patch(path, { action }); } catch { /* 403 etc. */ }
    if (typeof window !== "undefined") window.dispatchEvent(new Event(MDR_UNREAD_REFRESH));
    void load();
  }

  async function toggleIntakeExpand(intakeId: number) {
    if (expandedIntakeId === intakeId) {
      setExpandedIntakeId(null);
      setIntakeDetail(null);
      return;
    }
    setExpandedIntakeId(intakeId);
    setIntakeDetail(null);
    try {
      const res = await api.get<{ data: IntakeDetail }>(`/api/werkgroep-intakes/${intakeId}`);
      setIntakeDetail(res.data.data);
    } catch {
      setIntakeDetail(null);
    }
  }

  async function approveIntake(intakeId: number, note: string) {
    setIntakeBusy(true);
    try {
      await api.post(`/api/werkgroep-intakes/${intakeId}/approve`, { review_note: note || undefined });
      window.dispatchEvent(new Event(MDR_UNREAD_REFRESH));
      void load();
      setExpandedIntakeId(null);
      setIntakeDetail(null);
    } catch {
      // todo: show error
    } finally {
      setIntakeBusy(false);
    }
  }

  async function rejectIntake(intakeId: number, note: string) {
    setIntakeBusy(true);
    try {
      await api.post(`/api/werkgroep-intakes/${intakeId}/reject`, { review_note: note || undefined });
      window.dispatchEvent(new Event(MDR_UNREAD_REFRESH));
      void load();
      setExpandedIntakeId(null);
      setIntakeDetail(null);
    } catch {
      // todo: show error
    } finally {
      setIntakeBusy(false);
    }
  }

  async function markNoOpIntake(intakeId: number) {
    setIntakeBusy(true);
    try {
      await api.post(`/api/werkgroep-intakes/${intakeId}/mark-no-op`, {});
      window.dispatchEvent(new Event(MDR_UNREAD_REFRESH));
      void load();
      setExpandedIntakeId(null);
      setIntakeDetail(null);
    } catch {
      /* 400 / 403 */
    } finally {
      setIntakeBusy(false);
    }
  }

  async function generateLinkForGroup(groupId: number) {
    setLinkGenBusy(groupId);
    try {
      const res = await api.post<{
        data: {
          intake_id: number;
          url: string;
          group_id: number;
          group_name: string;
          recipient_email: string;
        };
      }>("/api/werkgroep-intakes/create-intake-link", { group_id: groupId });
      const d = res.data.data;
      setLinkByGroupId((prev) => ({
        ...prev,
        [groupId]: { url: d.url, intake_id: d.intake_id, recipient_email: d.recipient_email },
      }));
      setCopyToast("Link aangemaakt.");
      window.setTimeout(() => setCopyToast(null), 2200);
    } catch (e) {
      setCopyToast(readApiError(e));
      window.setTimeout(() => setCopyToast(null), 4500);
    } finally {
      setLinkGenBusy(null);
    }
  }

  async function generateAllIntakeLinks() {
    setLinkGenBusy("bulk");
    setBulkErrors([]);
    try {
      const res = await api.post<{
        data: {
          links: Array<{
            group_id: number;
            group_name: string;
            intake_id: number;
            url: string;
            recipient_email: string;
          }>;
          errors: Array<{ group_id: number; group_name: string; error: string }>;
        };
      }>("/api/werkgroep-intakes/bulk-create-intake-links", {});
      const { links, errors } = res.data.data;
      setLinkByGroupId((prev) => {
        const next = { ...prev };
        for (const l of links) {
          next[l.group_id] = {
            url: l.url,
            intake_id: l.intake_id,
            recipient_email: l.recipient_email,
          };
        }
        return next;
      });
      setBulkErrors(errors ?? []);
      const okN = links?.length ?? 0;
      const errN = errors?.length ?? 0;
      setCopyToast(
        errN ? `${okN} link(s) aangemaakt, ${errN} groep(en) overgeslagen (zie lijst hieronder).` : `${okN} link(s) aangemaakt.`
      );
      window.setTimeout(() => setCopyToast(null), 5000);
    } catch (e) {
      setCopyToast(readApiError(e));
      window.setTimeout(() => setCopyToast(null), 4500);
    } finally {
      setLinkGenBusy(null);
    }
  }

  async function copyToClipboard(text: string, toastLabel: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopyToast(toastLabel);
      window.setTimeout(() => setCopyToast(null), 2200);
    } catch {
      setCopyToast("Kopiëren mislukt (bijv. geen toestemming van de browser).");
      window.setTimeout(() => setCopyToast(null), 3200);
    }
  }

  function copyAllLinksAsTsv() {
    const lines = groups
      .map((g) => {
        const row = linkByGroupId[g.id];
        return row ? `${g.name}\t${row.url}` : null;
      })
      .filter((line): line is string => Boolean(line));
    if (!lines.length) {
      setCopyToast("Geen links om te kopiëren — genereer eerst link(s).");
      window.setTimeout(() => setCopyToast(null), 3000);
      return;
    }
    void copyToClipboard(lines.join("\n"), `${lines.length} regel(s) gekopieerd (groepnaam + tab + URL)`);
  }

  const LEDENLIJST_BUTTON_HTML_STYLE =
    "display: inline-block; padding: 0.55rem 1.1rem; font: 600 1rem system-ui, sans-serif; color: #fff; background: #ee2361; border-radius: 8px; text-decoration: none; border: none; cursor: pointer; flex: 0 0 auto;";

  const LEDENLIJST_BUTTONS_ROW_WRAPPER_STYLE =
    "display:flex;flex-wrap:wrap;gap:0.5rem;align-items:center;row-gap:0.5rem;";

  function escapeHtmlAttr(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
  }

  function escapeHtmlText(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  /** Eén HTML-blok: knoppen naast elkaar, met regelafbreking als de regel vol is (flex-wrap). */
  function copyAllLinksAsHtmlButtons() {
    const chunks: string[] = [];
    for (const g of groups) {
      const row = linkByGroupId[g.id];
      if (!row) continue;
      const href = escapeHtmlAttr(row.url);
      const label = escapeHtmlText(g.name);
      chunks.push(
        `<a href="${href}" role="button"\n  style="${escapeHtmlAttr(LEDENLIJST_BUTTON_HTML_STYLE)}">${label}</a>`
      );
    }
    if (!chunks.length) {
      setCopyToast("Geen links om te kopiëren — genereer eerst link(s).");
      window.setTimeout(() => setCopyToast(null), 3000);
      return;
    }
    const inner = chunks.join("\n");
    const wrapped = `<div style="${escapeHtmlAttr(LEDENLIJST_BUTTONS_ROW_WRAPPER_STYLE)}">\n${inner}\n</div>`;
    void copyToClipboard(wrapped, `${chunks.length} HTML-knop(pen) gekopieerd (naast elkaar, vullen de regel)`);
  }

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title-row" style={{ marginBottom: 2 }}>
            <h1 className="page-title" style={{ margin: 0 }}>
              Aanvragen
            </h1>
            <HelpHint label="Uitleg: aanvragen">
              Vier secties: <strong>Binnenkomend</strong> (verzoeken om contactgegevens of groepslidmaatschap),{" "}
              <strong>Mijn verzoeken</strong> (eigen antwoorden),{" "}
              <strong>Werkgroep intakes</strong> (ledenlijsten door voorzitters ingevuld) en{" "}
              <strong>Ledenlijst-links</strong> (intakelinks genereren en zelf delen, zonder uitnodigingsmail).
              Rode getallen op de tabs tonen hoeveel ongelezen waar van toepassing.
            </HelpHint>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div
        className="filters"
        style={{ marginBottom: "1rem", display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}
      >
        <button
          type="button"
          className={scope === "inbox" ? "btn-primary" : "btn-secondary"}
          onClick={() => setScope("inbox")}
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          Binnenkomend
          {unreadSplit != null && unreadSplit.inbox > 0 && (
            <span style={tabBadgeStyle}>{unreadSplit.inbox > 99 ? "99+" : unreadSplit.inbox}</span>
          )}
        </button>
        <button
          type="button"
          className={scope === "outbox" ? "btn-primary" : "btn-secondary"}
          onClick={() => setScope("outbox")}
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          Mijn verzoeken
          {unreadSplit != null && unreadSplit.outbox > 0 && (
            <span style={tabBadgeStyle}>{unreadSplit.outbox > 99 ? "99+" : unreadSplit.outbox}</span>
          )}
        </button>
        <button
          type="button"
          className={scope === "intakes" ? "btn-primary" : "btn-secondary"}
          onClick={() => setScope("intakes")}
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          Werkgroep intakes
          {unreadSplit != null && unreadSplit.intakes_pending > 0 && (
            <span
              style={tabBadgeStyle}
              title={`${unreadSplit.intakes_pending} ingediende ledenlijst(en) wachten op beoordeling`}
            >
              {unreadSplit.intakes_pending > 99 ? "99+" : unreadSplit.intakes_pending}
            </span>
          )}
        </button>
        <button
          type="button"
          className={scope === "ledenlinks" ? "btn-primary" : "btn-secondary"}
          onClick={() => setScope("ledenlinks")}
        >
          Ledenlijst-links
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-spinner" />
      ) : scope === "intakes" ? (
        <>
          <div style={{ marginBottom: "0.85rem", maxWidth: 800, fontSize: "0.85rem" }}>
            <span style={{ fontWeight: 600, marginRight: 12 }}>Filter op status:</span>
            {(["issued", "submitted", "approved", "rejected"] as const).map((s) => (
              <label
                key={s}
                style={{
                  marginRight: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={intakeStatusFilters.includes(s)}
                  onChange={(e) => {
                    setIntakeStatusFilters((prev) => {
                      if (e.target.checked) return prev.includes(s) ? prev : [...prev, s];
                      return prev.filter((x) => x !== s);
                    });
                  }}
                />
                {intakeStatusLabel(s)}
              </label>
            ))}
            <span style={{ marginLeft: 8, color: "#6b5f78", fontSize: "0.78rem" }}>
              (Standaard: ingediend + goedgekeurd. Dubbele “verstuurd”-regels per groep worden samengevoegd.)
            </span>
          </div>
          {intakes.length === 0 ? (
            <p className="page-subtitle">
              {intakeStatusFilters.length === 0 ? (
                <>
                  Nog geen werkgroep-intakes. Stuur een uitnodiging via de groepspagina (knop <em>Ledenlijst uitnodigen</em>) of
                  gebruik het tabblad <strong>Ledenlijst-links</strong> om een link te maken zonder e-mail.
                </>
              ) : (
                <>
                  Geen intakes voor de gekozen filter. Vink bijvoorbeeld ook <strong>Verstuurd</strong> aan om openstaande
                  links te zien, of pas de filter aan.
                </>
              )}
            </p>
          ) : (
            <ul className="data-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {intakes.map((intake) => {
              const needsAttention = intake.status === "submitted";
              const isExpanded = expandedIntakeId === intake.id;
              return (
                <li
                  key={intake.id}
                  className="detail-section"
                  style={{
                    marginBottom: "0.75rem",
                    padding: "1rem",
                    maxWidth: 800,
                    ...rowAttentionStyle(needsAttention),
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                      cursor: "pointer",
                    }}
                    onClick={() => { void toggleIntakeExpand(intake.id); }}
                  >
                    <strong>
                      <span
                        className="group-badge"
                        style={{
                          marginRight: 8,
                          fontSize: "0.7rem",
                          background:
                            intake.status === "submitted"
                              ? "var(--accent, #ee2361)"
                              : intake.status === "approved"
                              ? "#4caf50"
                              : intake.status === "rejected"
                              ? "#888"
                              : "#7c6e90",
                          color: "#fff",
                        }}
                      >
                        {intakeStatusLabel(intake.status)}
                      </span>
                      {intake.group_name}
                    </strong>
                    <span style={{ fontSize: "0.85rem", opacity: 0.8, display: "flex", alignItems: "center", gap: 8 }}>
                      {intake.submitted_at
                        ? `Ingediend: ${new Date(intake.submitted_at).toLocaleString("nl-NL")}`
                        : `Aangemaakt: ${new Date(intake.created_at).toLocaleString("nl-NL")}`}
                      <span style={{ color: "var(--accent, #ee2361)", fontSize: "0.8rem" }}>
                        {isExpanded ? "▲" : "▼"}
                      </span>
                    </span>
                  </div>

                  <div style={{ fontSize: "0.85rem", marginTop: "0.35rem", color: "#5c526d" }}>
                    Ontvanger:{" "}
                    <a href={`mailto:${intake.recipient_email}`} className="text-link">
                      {intake.recipient_email}
                    </a>
                    {intake.leden_count != null && (
                      <span style={{ marginLeft: 12 }}>{intake.leden_count} leden ingediend</span>
                    )}
                    {intake.sender_name && (
                      <span style={{ marginLeft: 12 }}>Door: {intake.sender_name}</span>
                    )}
                  </div>

                  {isExpanded && (
                    <div style={{ marginTop: "0.75rem", borderTop: "1px solid #e4dfe8", paddingTop: "0.75rem" }}>
                      {intakeDetail?.id === intake.id ? (
                        <IntakeDetailPanel
                          intakeId={intake.id}
                          intake={intakeDetail}
                          onApprove={approveIntake}
                          onReject={rejectIntake}
                          onMarkNoOp={markNoOpIntake}
                          onRefreshDetail={async () => {
                            const res = await api.get<{ data: IntakeDetail }>(`/api/werkgroep-intakes/${intake.id}`);
                            setIntakeDetail(res.data.data);
                          }}
                          onRevisionSent={async () => {
                            window.dispatchEvent(new Event(MDR_UNREAD_REFRESH));
                            setExpandedIntakeId(null);
                            setIntakeDetail(null);
                            void fetchUnreadCounts();
                            const r = await api.get<{ data: IntakeRow[] }>(
                              `/api/werkgroep-intakes${werkgroepIntakesApiQuery(intakeStatusFilters)}`
                            );
                            setIntakes(r.data.data ?? []);
                          }}
                          busy={intakeBusy}
                        />
                      ) : (
                        <div className="loading-spinner" style={{ width: 24, height: 24 }} />
                      )}
                    </div>
                  )}

                  <p style={{ marginTop: "0.5rem" }}>
                    <Link to={`/groups/${intake.group_id}`} className="text-link">
                      → Groep bekijken
                    </Link>
                  </p>
                </li>
              );
            })}
          </ul>
          )}
        </>
      ) : scope === "ledenlinks" ? (
        <div style={{ maxWidth: 920 }}>
          {copyToast && (
            <p
              role="status"
              className="page-subtitle"
              style={{
                marginBottom: "0.75rem",
                padding: "0.5rem 0.65rem",
                borderRadius: 8,
                background: "color-mix(in srgb, var(--accent, #ee2361) 10%, transparent)",
                border: "1px solid var(--surface-300, #e4dfe8)",
              }}
            >
              {copyToast}
            </p>
          )}
          <p className="page-subtitle" style={{ marginBottom: "0.75rem" }}>
            Hier maak je dezelfde <strong>intakelinks</strong> als bij <em>Ledenlijst uitnodigen</em> op de groepspagina,
            maar <strong>zonder uitnodigingsmail</strong> — handig om links op een interne pagina of via eigen kanalen te
            delen. Links worden <strong>voor elke groep</strong> aangemaakt; ontbreekt een voorzitter-mail, dan wordt er
            geen automatische mail bij goedkeuren/afwijzen gestuurd (de link werkt wel). Een gegenereerde link is{" "}
            <strong>{WERKGROEP_INTAKE_LINK_VALIDITY_DAYS} dagen</strong> geldig (zelfde termijn als bij uitnodigen per
            mail).
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
              marginBottom: "1rem",
              alignItems: "center",
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              disabled={linkGenBusy != null || groups.length === 0}
              onClick={() => {
                void generateAllIntakeLinks();
              }}
            >
              {linkGenBusy === "bulk" ? "Bezig met genereren…" : "Genereer links voor alle groepen"}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={Object.keys(linkByGroupId).length === 0}
              onClick={() => copyAllLinksAsTsv()}
            >
              Kopieer alle links
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={Object.keys(linkByGroupId).length === 0}
              onClick={() => copyAllLinksAsHtmlButtons()}
              title="Plakken in WordPress of HTML: knoppen naast elkaar, nieuwe regel als de regel vol is"
            >
              Kopieer als HTML-knoppen
            </button>
          </div>
          {bulkErrors.length > 0 && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "0.65rem 0.85rem",
                borderRadius: 8,
                background: "#fff7ed",
                border: "1px solid #fdba74",
                fontSize: "0.88rem",
              }}
            >
              <strong>Niet voor elke groep kon een link worden gemaakt:</strong>
              <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.2rem" }}>
                {bulkErrors.map((e) => (
                  <li key={e.group_id}>
                    <strong>{e.group_name}</strong> — {e.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {groups.length === 0 ? (
            <p className="page-subtitle">Geen groepen in dit overzicht.</p>
          ) : (
            <ul className="data-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {groups.map((g) => {
                const generated = linkByGroupId[g.id];
                const rowBusy = linkGenBusy === g.id;
                return (
                  <li
                    key={g.id}
                    className="detail-section"
                    style={{ marginBottom: "0.75rem", padding: "1rem", maxWidth: 900 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        gap: "0.5rem",
                        alignItems: "center",
                      }}
                    >
                      <strong>{g.name}</strong>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={linkGenBusy != null}
                          onClick={() => {
                            void generateLinkForGroup(g.id);
                          }}
                        >
                          {rowBusy ? "Even geduld…" : "Genereer link"}
                        </button>
                        {generated && (
                          <button
                            type="button"
                            className="btn-primary btn-sm"
                            onClick={() => {
                              void copyToClipboard(generated.url, "Link gekopieerd");
                            }}
                          >
                            Kopieer link
                          </button>
                        )}
                      </div>
                    </div>
                    {generated && (
                      <div style={{ marginTop: "0.65rem", fontSize: "0.82rem", color: "#5c526d" }}>
                        {isWerkgroepIntakePlaceholderRecipientEmail(generated.recipient_email) ? (
                          <span>
                            <strong>Geen voorzitter-mail</strong> bekend in DomAddress — automatische mail bij
                            goedkeuren/afwijzen wordt overgeslagen; gebruik de gedeelde link om de voorzitter te bereiken.
                          </span>
                        ) : (
                          <span>
                            E-mail bij afhandeling (voorzitter):{" "}
                            <a href={`mailto:${generated.recipient_email}`} className="text-link">
                              {generated.recipient_email}
                            </a>
                          </span>
                        )}
                        <p style={{ margin: "0.45rem 0 0", wordBreak: "break-all", fontFamily: "ui-monospace, monospace", fontSize: "0.78rem" }}>
                          {generated.url}
                        </p>
                        <p style={{ margin: "0.35rem 0 0", fontSize: "0.78rem" }}>
                          <Link to={`/aanvragen?tab=intakes&intake=${generated.intake_id}`} className="text-link">
                            → Intake in werkgroep-tab openen
                          </Link>
                          {" · "}
                          <Link to={`/groups/${g.id}`} className="text-link">
                            → Groep bekijken
                          </Link>
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : rows.length === 0 ? (
        <p className="page-subtitle">Nog geen verzoeken in dit overzicht.</p>
      ) : (
        <ul className="data-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {rows.map((r) => {
            const inboxOpen = r.role === "inbox" && !r.read_by_chair_at;
            const outboxUnread = r.role === "outbox" && !!r.response_unread;
            const needsAttention = inboxOpen || outboxUnread;
            return (
              <li
                key={`${r.request_kind}-${r.id}`}
                className="detail-section"
                style={{
                  marginBottom: "0.75rem",
                  padding: "1rem",
                  maxWidth: 800,
                  ...rowAttentionStyle(needsAttention),
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
                  <strong>
                    <span className="group-badge" style={{ marginRight: 8, fontSize: "0.7rem" }}>
                      {kindLabel(r.request_kind)}
                    </span>
                    {r.group_name} — {r.target_label}
                  </strong>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem", opacity: 0.8 }}>
                    {inboxOpen && (
                      <span
                        className="group-badge"
                        style={{ background: "var(--accent, #ee2361)", color: "#fff", fontSize: "0.7rem", padding: "0.1rem 0.45rem", borderRadius: 4 }}
                      >
                        Open verzoek
                      </span>
                    )}
                    {r.role === "outbox" && r.response_unread && (
                      <span
                        className="group-badge"
                        style={{ background: "var(--accent, #ee2361)", color: "#fff", fontSize: "0.7rem", padding: "0.1rem 0.45rem", borderRadius: 4 }}
                      >
                        Nieuw antwoord
                      </span>
                    )}
                    {new Date(r.created_at).toLocaleString("nl-NL")}
                  </span>
                </div>
                {r.role === "inbox" && (
                  <p style={{ margin: "0.5rem 0" }}>
                    Van: {r.requester_name}{" "}
                    <a href={`mailto:${r.requester_email}`} className="text-link">
                      {r.requester_email}
                    </a>
                  </p>
                )}
                {r.message && <p style={{ margin: "0.5rem 0" }}><em>Bericht:</em> {r.message}</p>}
                {r.role === "inbox" && r.read_by_chair_at && (
                  <p className="page-subtitle" style={{ margin: "0.5rem 0" }}>
                    <strong>Status:</strong> {r.resolution ? resolutionLabel(r.resolution) : "Afgehandeld"} —{" "}
                    {new Date(r.read_by_chair_at).toLocaleString("nl-NL")}
                  </p>
                )}
                {r.role === "inbox" && !r.read_by_chair_at && (
                  <p style={{ marginTop: "0.75rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                    <button type="button" className="btn-primary btn-sm" onClick={() => resolveRow(r, "approve")}>
                      Goedkeuren
                    </button>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => resolveRow(r, "reject")}>
                      Afwijzen
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => resolveRow(r, "acknowledge")}
                      title="Markeer als gezien zonder goed- of afkeur"
                    >
                      Alleen afhandelen
                    </button>
                  </p>
                )}
                {r.role === "outbox" && !r.read_by_chair_at && (
                  <p className="page-subtitle" style={{ margin: "0.5rem 0" }}>
                    <strong>Status:</strong> nog wachten op afhandeling.
                  </p>
                )}
                {r.role === "outbox" && r.read_by_chair_at && r.resolution && (
                  <p className="page-subtitle" style={{ margin: "0.5rem 0" }}>
                    <strong>Antwoord:</strong> {resolutionLabel(r.resolution)} —{" "}
                    {new Date(r.read_by_chair_at).toLocaleString("nl-NL")}
                  </p>
                )}
                <p style={{ marginTop: "0.5rem" }}>
                  <Link to={`/groups/${r.group_id}`} className="text-link">
                    → Groep bekijken
                  </Link>
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
